/**
 * Beach painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const beachPaintingIdeasPack: ToilePack = {
    slug: 'beach-painting-ideas',
    category: 'craft',
    name: 'Beach painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'shore', label: 'Foam shore', subject: 'a canvas of turquoise water lapping onto sand seen from above, lace foam along the edge'},
        {key: 'umbrellas', label: 'Beach umbrellas', subject: 'a canvas of striped beach umbrellas and towels seen from high above'},
        {key: 'palm', label: 'Palm sunset', subject: 'a canvas of one leaning palm against an orange and pink sunset over the sea'},
    ],
    styles: [
        {key: 'easy', label: 'Easy acrylic', style: 'easy beginner acrylic with flat bold shapes and simple clean blends', palette: 'bright clean colors with white highlights on the water', framing: 'The canvas on a wooden easel seen straight on, a plain pale wall behind'},
        {key: 'knife', label: 'Palette knife', style: 'thick palette-knife impasto with bold ridged strokes of paint', palette: 'rich saturated colors laid on in raised strokes', framing: 'The canvas leaning against a white wall on a light wooden floor'},
        {key: 'realism', label: 'Soft realism', style: 'soft realistic acrylic with smooth gradients and fine detail', palette: 'natural colors blended gently from light to deep', framing: 'The canvas lying flat on a wooden work table seen from directly above, brushes and a paint palette beside it'},
    ],
    directions: ['seashells', 'night beach', 'with a pier'],
    directionHint: 'try: seashells',
    seo: {
        title: 'Beach Painting Ideas: Foam Shore, Umbrellas, Palm Sunset',
        metaDescription: 'Nine beach painting ideas for a holiday wall: turquoise foam on sand, striped umbrellas from above and a palm at sunset, in three styles.',
        h1: 'Beach painting ideas: foam on sand, striped umbrellas, palm at sunset',
        tagline: 'Nine beach canvases, three holiday views, each in easy acrylic, palette-knife impasto or soft realism.',
        intro: 'A beach canvas brings a holiday home, all turquoise water and warm sand. These nine show three views: turquoise water lapping onto sand, seen from above with lace foam along the edge; striped beach umbrellas and towels seen from high above; and one leaning palm against an orange and pink sunset over the sea. Type another subject, such as seashells or a night beach, and the same holiday look carries it.',
        useCases: [
            'a holiday reminder over a desk',
            'a hallway canvas',
            'a gift for someone who misses the sea',
            'a first seascape',
        ],
        faq: [
            {q: 'Which views are seen from above?', a: 'The foam shore and the umbrellas are both seen from above. The palm is a sunset seen at the horizon.'},
            {q: 'What is the palette-knife style?', a: 'Thick, ridged strokes of rich, saturated color laid on with a knife.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the beach palette and the three styles carry it.'},
            {q: 'Where are the canvases shown?', a: 'On a wooden easel, leaning against a white wall on a light wooden floor, or lying flat on a work table seen from above.'},
        ],
    },
}
